import { useEffect, useMemo, useRef, useState } from "react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { useSnackbar } from "notistack";
import dayjs from "@lib/dayjs";
import { useSelector } from "react-redux";
import PaperSendIcon from "@assets/svg/PaperSendIcon";
import useChat from "@custom-hooks/useChat";
import { identity } from "@utils/mergeMessages";
import { EnumUserTypes, userTypes } from "@utils/constants";

const MessagesTab = ({ applicationDetails }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector((state) => state.auth);
  const receiverDetails = useMemo(
    () => ({
      ...applicationDetails?.candidateDetails,
      userId: applicationDetails?.recruiterId,
      userType: EnumUserTypes.RECRUITER,
    }),
    [applicationDetails?.candidateDetails, applicationDetails?.recruiterId]
  );
  const { isMessageLoading, messages, sendMessage } = useChat({
    jobId: applicationDetails?._id,
    receiverDetails,
  });
  const [isComposing, setIsComposing] = useState(false);
  const [message, setMessage] = useState("");
  // const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const pollingInterval = 3000;
  const messagesContainerRef = useRef(null);

  const scrollToBottom = (smooth = true) => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  useEffect(() => {
    if (applicationDetails) {
      if (applicationDetails.recruiterId) {
        setSelectedContact({
          id: applicationDetails.recruiterId,
          name: applicationDetails.recruiterName || "Recruiter",
        });
      } else if (applicationDetails.referralId) {
        setSelectedContact({
          id: applicationDetails.referralId,
          name: applicationDetails.referralName || "Referral",
        });
      }
    }
  }, [applicationDetails]);

  const handleContactSelect = (contactId, contactName) => {
    setSelectedContact({
      id: contactId,
      name: contactName,
    });
  };

  useEffect(() => {
    if (messages?.length) {
      scrollToBottom();
    }
  }, [messages]);

  console.log('application deatils',applicationDetails)


  if (isMessageLoading) {
    return (
      <div className="flex justify-center mt-40">
        <LoadingAnimation color="black" />
      </div>
    );
  }


  return (
    <div className="flex flex-1 gap-6 pr-20 overflow-hidden">
      <div className="border-r border-[#ECECEC] w-1/4">
        {applicationDetails?.recruiterName && (
          <div
            className={`flex items-center gap-3 py-3 px-4 border-b border-[#ECECEC] cursor-pointer ${
              selectedContact?.id === applicationDetails.recruiterId
                ? "bg-[#F1F5F9]"
                : ""
            }`}
            onClick={() =>
              handleContactSelect(
                applicationDetails.recruiterId,
                applicationDetails.recruiterName
              )
            }
          >
            <div className="font-montserrat text-white font-semibold uppercase w-9 h-9 rounded-full bg-gray-400 flex items-center justify-center text-xl">
              {applicationDetails.recruiterName.charAt(0)}
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-charcoal font-medium text-xs">
                {applicationDetails.recruiterName}
              </p>
              <p className="text-light-black text-xs">Recruiter</p>
            </div>
          </div>
        )}
        {applicationDetails?.referralName && (
          <div
            className={`flex items-center gap-3 py-3 px-4 border-b border-[#ECECEC] cursor-pointer ${
              selectedContact?.id === applicationDetails.referralId
                ? "bg-[#F1F5F9]"
                : ""
            }`}
            onClick={() =>
              handleContactSelect(
                applicationDetails.referralId,
                applicationDetails.referralName
              )
            }
          >
            <div className="font-montserrat text-white font-semibold uppercase w-9 h-9 rounded-full bg-gray-400 flex items-center justify-center text-xl">
              {applicationDetails.referralName.charAt(0)}
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-charcoal font-medium text-xs">
                {applicationDetails.referralName}
              </p>
              <p className="text-light-black text-xs">Referral</p>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col h-full w-full relative">
        <div
          ref={messagesContainerRef}
          className="flex-grow overflow-y-auto no-scrollbar pr-2 pb-4"
          style={{ maxHeight: "calc(100% - 100px)" }}
        >
          {messages?.length ? (
            messages?.map((item, index) => {
              const isCurrentUser =
                item?.createdBy ===
                (user?.userType === userTypes.RECRUITER
                  ? user?._id
                  : user?.userId);
              // const isSentByCurrentUser = item.createdBy === user._id;
              return (
                <div
                  key={identity(item) || index}
                  className="flex flex-col gap-2 rounded cursor-pointer text-xs border-b p-4"
                >
                  <div
                    className={`rounded text-charcoal flex justify-between font-bold`}
                  >
                    <p className="text-[13px] font-bold text-charcoal">
                      {item.createdName}
                    </p>
                    <p className="text-charcoal font-medium italic">
                      {dayjs(item?.createdAt).format("HH:mm DD/MM/YYYY")}
                    </p>
                  </div>
                  <div className="flex justify-between items-end">
                    <p
                      className={`text-xs font-medium text-light-black py-3 px-5 rounded`}
                    >
                      {item.message}
                    </p>
                    {isCurrentUser && item.seen && (
                      <p className="text-xss text-green-600">Seen</p>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-center flex h-full items-end justify-center pb-10">
              No messages available.
            </p>
          )}
        </div>

        {/* Fixed message input area */}
        <div className="absolute bottom-0 left-0 right-0 bg-white mx-5 mb-2">
          <div className="flex gap-4">
            <textarea
              className="p-4 w-full bg-white rounded text-xs font-medium border border-[#ECECEC] focus:border-gray-300 focus:outline-none"
              rows={4}
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            ></textarea>
            <button
              className="text-xs text-charcoal font-medium flex items-center disabled:cursor-not-allowed"
              onClick={() => sendMessage({ message, setMessage })}
              disabled={isLoading || !message.trim()}
            >
              {isLoading ? (
                <LoadingAnimation color="black" className={"w-4 h-4"} />
              ) : (
                <PaperSendIcon fill={message.trim() && "#FF9800"} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesTab;
